import { useEffect, useState } from "react";
import { Box, Grid, Typography } from "@mui/joy";

import { AlbumWidget } from "../../../components";
import { useTask } from "../../../hooks/query";
import { API_ENDPOINTS } from "../../../constants";

interface IPropertyPhotos {
    taskDetails: {
        Id: string;
        ProjectId: string;
    };
}

export function PermitPhotos(props: IPropertyPhotos) {
    const { taskDetails } = { ...props };
    const { useListAssets } = useTask();
    const listAssetsSvc = useListAssets();

    const [imageGroups, setImageGroups] = useState<any>({});

    const fetchData = async () => {
        try {
            if (!taskDetails.Id) return;

            const params = {
                TaskId: taskDetails.Id,
                AssetType: "TASK:BUILDING_PERMIT",
            };
            const response = await listAssetsSvc.mutateAsync(params);
            return response;
        } catch (e) {
            console.error(e);
        }
    };

    const groupImages = (serverImages: any[]) => {
        // Build grouped images
        const grouped: any = {};

        Object.entries(roofingPhotoSections).forEach(([groupKey, sections]: any) => {
            grouped[groupKey] = sections.map((section: any) => {
                const groupAssets = serverImages.filter((serverImage: any) => serverImage.Label === section.label);
                return {
                    ...section,
                    images: groupAssets.map((asset: any) => API_ENDPOINTS.asset.getImageAsSrc(asset.AssetId)),
                };
            });
        });

        setImageGroups(grouped);
    };

    useEffect(() => {
        fetchData().then((assetData: any) => {
            if (assetData) groupImages(assetData);
        });
    }, []);

    const sectionTitles: Record<string, string> = {
        beforeWork: "Before Work",
        duringWork: "During Work",
        afterWork: "After Work",
    };

    return (
        <Box sx={{ mx: "auto", mt: 0 }}>
            {Object.keys(imageGroups).map((groupKey) => (
                <Box key={groupKey} sx={{ mb: 3 }}>
                    <Typography level="title-lg" sx={{ mb: 1 }}>
                        {sectionTitles[groupKey]}
                    </Typography>

                    <Grid container spacing={2}>
                        {imageGroups[groupKey].map((section: any) => (
                            <Grid xs={12} key={section.id}>
                                <AlbumWidget {...section} taskId={taskDetails.Id} projectId={taskDetails.ProjectId} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ))}
        </Box>
    );
}

const roofingPhotoSections = {
    beforeWork: [
        {
            id: "before_frontProperty",
            title: "Front of Property – Before Work",
            label: "TASK:BUILDING_PERMIT:FHSA",
            description:
                "Take a wide photo showing the entire front of the property, including the visible address, before any construction or roofing work begins.",
            coverImage: "https://frontgate.scene7.com/is/image/frontgate/b-75716_main?$wfih$",
        },
        {
            id: "before_roofOverview",
            title: "Roof Overview – Existing Condition",
            label: "TASK:BUILDING_PERMIT:ROOF_OVERVIEW_BEFORE",
            description:
                "Capture a wide shot of the existing roof showing its overall condition and materials before removal or repair.",
            coverImage: "https://frontgate.scene7.com/is/image/frontgate/b-75716_main?$wfih$",
        },
        {
            id: "before_closeUpRoof",
            title: "Close-Up – Roof Surface (Before Work)",
            label: "TASK:BUILDING_PERMIT:ROOF_CLOSEUP_BEFORE",
            description:
                "Take detailed photos of the existing roof surface, including shingles, tiles, or other materials showing wear or damage.",
            coverImage: "https://frontgate.scene7.com/is/image/frontgate/b-75716_main?$wfih$",
        },
        {
            id: "before_eavesFascia",
            title: "Eaves / Fascia / Soffit – Before Work",
            label: "TASK:BUILDING_PERMIT:EAVES_BEFORE",
            description: "Capture clear images of the eaves, fascia, and soffit areas to document pre-work conditions.",
            coverImage: "https://frontgate.scene7.com/is/image/frontgate/b-75716_main?$wfih$",
        },
        {
            id: "before_sideRear",
            title: "Side and Rear Elevations – Before Work",
            label: "TASK:BUILDING_PERMIT:SIDE_REAR_BEFORE",
            description: "Take wide photos showing the side and rear of the structure before work begins.",
            coverImage: "https://frontgate.scene7.com/is/image/frontgate/b-75716_main?$wfih$",
        },
    ],

    duringWork: [
        {
            id: "during_roofDecking",
            title: "Roof Decking – In Progress",
            label: "TASK:BUILDING_PERMIT:ROOF_DECKING",
            description:
                "Take photos showing the roof decking after tear-off to document sheathing condition before new material installation.",
            coverImage: "https://frontgate.scene7.com/is/image/frontgate/b-75716_main?$wfih$",
        },
        {
            id: "during_underlayment",
            title: "Underlayment Installation – In Progress",
            label: "TASK:BUILDING_PERMIT:UNDERLAYMENT",
            description:
                "Capture photos showing proper installation of felt or synthetic underlayment across the roof surface.",
            coverImage: "https://frontgate.scene7.com/is/image/frontgate/b-75716_main?$wfih$",
        },
        {
            id: "during_flashingVents",
            title: "Flashing and Vent Installation – In Progress",
            label: "TASK:BUILDING_PERMIT:FLASHING_VENTS",
            description:
                "Take photos of step flashing, pipe jacks, valley metal, and other flashing or vent installations to confirm compliance.",
            coverImage: "https://frontgate.scene7.com/is/image/frontgate/b-75716_main?$wfih$",
        },
        {
            id: "during_fasteners",
            title: "Nailing / Fastener Pattern – In Progress",
            label: "TASK:BUILDING_PERMIT:FASTENERS",
            description:
                "Provide close-up images showing nail or fastener spacing and placement for building code verification.",
            coverImage: "https://frontgate.scene7.com/is/image/frontgate/b-75716_main?$wfih$",
        },
    ],

    afterWork: [
        {
            id: "after_frontProperty",
            title: "Front of Property – After Work",
            label: "TASK:BUILDING_PERMIT:FHSA_AFTER",
            description:
                "Take a wide photo showing the entire front of the property, including the visible address, after all roofing work has been completed.",
            coverImage: "https://frontgate.scene7.com/is/image/frontgate/b-75716_main?$wfih$",
        },
        {
            id: "after_roofOverview",
            title: "Roof Overview – Completed Work",
            label: "TASK:BUILDING_PERMIT:ROOF_OVERVIEW_AFTER",
            description: "Capture a wide photo showing the finished roof surface, including all slopes and ridges.",
            coverImage: "https://frontgate.scene7.com/is/image/frontgate/b-75716_main?$wfih$",
        },
        {
            id: "after_closeUpDetails",
            title: "Close-Up – Flashing, Vents, and Edges",
            label: "TASK:BUILDING_PERMIT:ROOF_DETAILS_AFTER",
            description:
                "Take close-up photos showing final flashing, vent seals, drip edges, and other finishing details.",
            coverImage: "https://frontgate.scene7.com/is/image/frontgate/b-75716_main?$wfih$",
        },
        {
            id: "after_guttersEaves",
            title: "Gutters, Eaves, and Ridge Details – Completed Work",
            label: "TASK:BUILDING_PERMIT:GUTTERS_AFTER",
            description:
                "Capture photos of gutters, eaves, ridges, and other components to confirm proper installation and finishing.",
            coverImage: "https://frontgate.scene7.com/is/image/frontgate/b-75716_main?$wfih$",
        },
        {
            id: "after_permitSticker",
            title: "Permit Sticker / Final Inspection Tag",
            label: "TASK:BUILDING_PERMIT:PERMIT_TAG",
            description:
                "Take a clear photo of the posted permit or final inspection sticker as required by the building department.",
            coverImage: "https://frontgate.scene7.com/is/image/frontgate/b-75716_main?$wfih$",
        },
    ],
};
